// Quotes.jsx — BM-H77 Phase-2 Quotes Tracker.
//
// The daily-driver operational tracker at /quotes for Emily & Rebekah.
// Model mirrors PaymentTracker.jsx (Brief 50) — same filter-bar shape,
// same table density, same 60s auto-refresh cadence — so fee earners
// already familiar with the payment tracker have zero relearn.
//
// Nine columns per V's spec: Ref, Client, Matter, FE, Value, Sent,
// Received (with the SHIP-BLOCKER verbatim tooltip on the header),
// Accepted, Next step.
//
// Row state machine (Next step column) is derived from quote.status +
// quote.accepted_at + quote.conversion. See _row_state / _next_step
// helpers below.
//
// The Received column reads receipts in one batch call after the list
// loads (POST /api/hub/quotes/receipts/batch) so the tracker doesn't
// N+1 the backend on every refresh.

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Loader2, Search, RefreshCw, Clock, ChevronRight,
  Plus, HelpCircle,
} from "lucide-react";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { STAFF } from "../config/staffConfig";
import { Button } from "../components/ui/button";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";

const REFRESH_INTERVAL_MS = 60 * 1000;

// Verbatim SHIP-BLOCKER string from HANDOFF (Received-column tooltip).
// Do not paraphrase — V confirmed exact wording.
const RECEIVED_TOOLTIP_TEXT =
  "Some mail clients (Outlook desktop, iOS Mail) block remote images by " +
  "default. A blank Received cell doesn't mean the client hasn't read the " +
  "quote — it means their mail client didn't render our tracking pixel.";

const FILTER_CHIPS = [
  { key: "waiting_on_us",     label: "Waiting on us",     defaultOn: true },
  { key: "waiting_on_client", label: "Waiting on client", defaultOn: true },
  { key: "draft",             label: "Draft",             defaultOn: false },
  { key: "converted",         label: "Converted",         defaultOn: false },
  { key: "declined",          label: "Declined",          defaultOn: false },
  { key: "expired",           label: "Expired",           defaultOn: false },
];

function authHeaders() {
  const uid =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") || "";
  const email =
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") || uid;
  return {
    "X-Bonsai-User-Id": uid || email,
    "X-User-Email": email,
  };
}

function fmtGbp(pounds) {
  const n = Number(pounds);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP",
    // Pence only when there are pence (the £12.50 bank transfer fees).
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  }).format(n);
}

function fmtTimeOrDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return "";
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) {
    return `\u2713 ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  }
  return `\u2713 ${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`;
}

// Turn a Phase-1 quote doc (client stores first_name/last_name) into a
// tracker-friendly display name. Falls back to the raw email if neither
// is present so a mis-migrated row still renders identifiably.
function clientDisplayName(client) {
  if (!client) return "";
  const explicit = (client.name || "").trim();
  if (explicit) return explicit;
  const composed = [client.first_name, client.last_name].filter(Boolean).join(" ").trim();
  return composed || (client.email || "").trim();
}

function truncateAddress(addr, max = 40) {
  if (!addr) return "";
  return addr.length <= max ? addr : addr.slice(0, max - 1) + "\u2026";
}

// Derive the abstract state the row is currently in. This drives:
//   * the priority sort (see PRIORITY_ORDER)
//   * the filter-chip membership (waiting_on_us / waiting_on_client)
//   * the Next-step cell content
export function deriveRowState(quote) {
  const status = quote.status || "draft";
  const accepted = Boolean(quote.accepted_at);
  const convo = quote.conversion || {};
  if (status === "draft") return "draft";
  if (status === "sent") {
    if (!accepted) return "sent_awaiting";
    if (!convo.conflict_check_id) return "accepted_no_check";
    if (!convo.matter_id) return "check_in_progress";
  }
  if (status === "converted") return "converted";
  if (status === "declined") return "declined";
  if (status === "expired") return "expired";
  return status;
}

// Chip membership — the filter chips group the row-states into two
// coarse buckets ("waiting on us" vs "waiting on client") plus a
// straight passthrough for draft/converted/declined/expired.
function rowStateChips(state) {
  switch (state) {
    case "draft": return ["draft"];
    case "sent_awaiting": return ["waiting_on_client"];
    case "accepted_no_check": return ["waiting_on_us"];
    case "check_in_progress": return ["waiting_on_us"];
    case "converted": return ["converted"];
    case "declined": return ["declined"];
    case "expired": return ["expired"];
    default: return [];
  }
}

// Priority order — quotes needing our team's attention float to the top,
// then quotes waiting on the client, then drafts, then closed rows.
const PRIORITY_ORDER = [
  "accepted_no_check", "check_in_progress",  // amber — action on us
  "sent_awaiting",                            // waiting on client
  "draft",                                    // half-written on us
  "converted", "declined", "expired",         // closed
];
function priorityRank(state) {
  const idx = PRIORITY_ORDER.indexOf(state);
  return idx === -1 ? PRIORITY_ORDER.length : idx;
}

function NextStepCell({ quote, navigate }) {
  const state = deriveRowState(quote);
  const ref = quote.quote_ref;
  const feFirst = (quote.fee_earner?.full_name || "").split(" ")[0] || "";
  if (state === "draft") {
    return (
      <Button size="sm" onClick={() => navigate(`/quotes/${ref}`)}
        data-testid={`quotes-tracker-next-send-${ref}`}
        className="bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 text-xs px-2 py-1">
        Send quote &rarr;
      </Button>
    );
  }
  if (state === "sent_awaiting") return <span className="text-gray-500 text-xs">&mdash;</span>;
  if (state === "accepted_no_check") {
    return (
      <Button size="sm" onClick={() => navigate(`/quotes/${ref}`)}
        data-testid={`quotes-tracker-next-conflict-${ref}`}
        className="bg-status-amber/15 text-status-amber hover:bg-status-amber/25 text-xs px-2 py-1">
        Start conflict check &rarr;
      </Button>
    );
  }
  if (state === "check_in_progress") {
    return (
      <span data-testid={`quotes-tracker-next-progress-${ref}`} className="text-gray-400 text-xs">
        Conflict check in progress {feFirst && `(${feFirst})`}
      </span>
    );
  }
  if (state === "converted") {
    const matterRef = quote.conversion?.matter_id;
    return (
      <a href={`/matter/${matterRef}`}
        data-testid={`quotes-tracker-next-matter-${ref}`}
        className="text-teal hover:underline text-xs">
        Matter opened &mdash; {matterRef}
      </a>
    );
  }
  if (state === "declined") {
    const reason = (quote.declined_reason || "").slice(0, 30);
    return (
      <span data-testid={`quotes-tracker-next-declined-${ref}`} className="text-status-red/70 text-xs">
        Declined {reason && `\u2014 ${reason}`}
      </span>
    );
  }
  if (state === "expired") {
    return (
      <Button size="sm" variant="ghost" onClick={() => navigate(`/quotes/${ref}`)}
        data-testid={`quotes-tracker-next-reissue-${ref}`}
        className="text-gray-400 hover:text-white text-xs px-2 py-1">
        Reissue &rarr;
      </Button>
    );
  }
  return null;
}

function ReceivedCell({ receipt }) {
  if (!receipt || !receipt.first_opened_at) return <span className="text-gray-600">&nbsp;</span>;
  return <span className="text-teal text-xs">{fmtTimeOrDate(receipt.first_opened_at)}</span>;
}

function AcceptedCell({ quote, onMarkAccepted, busy }) {
  const state = deriveRowState(quote);
  if (quote.accepted_at) {
    return <span className="text-teal text-xs">{fmtTimeOrDate(quote.accepted_at)}</span>;
  }
  if (state === "sent_awaiting") {
    return (
      <Button size="sm" disabled={busy}
        onClick={(e) => { e.stopPropagation(); onMarkAccepted(quote); }}
        data-testid={`quotes-tracker-mark-accepted-${quote.quote_ref}`}
        className="bg-teal/15 text-teal hover:bg-teal/25 text-xs px-2 py-1">
        Mark accepted
      </Button>
    );
  }
  return <span className="text-gray-600">&nbsp;</span>;
}

function FeChip({ initials }) {
  if (!initials) return <span className="text-gray-500 text-xs">&mdash;</span>;
  const palette = {
    EH: "bg-purple-500/15 text-purple-300 border-purple-500/30",
    RM: "bg-teal/15 text-teal border-teal/30",
    JD: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    VEC: "bg-status-amber/15 text-status-amber border-status-amber/30",
  };
  const cls = palette[initials] || "bg-slate-500/15 text-slate-300 border-slate-500/30";
  return (
    <span data-testid={`quotes-tracker-fe-chip-${initials}`}
      className={`px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
      {initials}
    </span>
  );
}

function ReceivedHeader() {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid="quotes-tracker-received-header"
            className="inline-flex items-center gap-1 cursor-help">
            Received <HelpCircle className="w-3 h-3 text-gray-500" />
          </span>
        </TooltipTrigger>
        <TooltipContent
          data-testid="quotes-tracker-received-tooltip"
          className="max-w-xs text-xs leading-relaxed">
          {RECEIVED_TOOLTIP_TEXT}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function TrackerRow({ quote, receipt, navigate, onMarkAccepted, busyRef }) {
  const client = quote.client || {};
  const isNewContact = !client.smart_contact_id;
  const property = quote.property || {};
  const feeEarner = quote.fee_earner || {};
  const totalEx = quote.totals?.fees_ex_vat || 0;
  const busy = busyRef === quote.quote_ref;

  return (
    <tr
      className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
      onClick={() => navigate(`/quotes/${quote.quote_ref}`)}
      data-testid={`quotes-tracker-row-${quote.quote_ref}`}
    >
      <td className="py-2.5 px-3 text-xs font-mono">
        <a href={`/quotes/${quote.quote_ref}`} onClick={(e) => e.stopPropagation()}
          data-testid={`quotes-tracker-ref-link-${quote.quote_ref}`}
          className="text-teal hover:underline">{quote.quote_ref}</a>
        {quote.is_test && (
          <span data-testid={`quotes-tracker-test-chip-${quote.quote_ref}`}
            className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] uppercase font-semibold">
            Test
          </span>
        )}
      </td>
      <td className="py-2.5 px-3 text-sm text-white">
        {clientDisplayName(client)}
        {isNewContact && (
          <span data-testid={`quotes-tracker-new-pill-${quote.quote_ref}`}
            className="ml-1.5 px-1.5 py-0.5 rounded-full bg-status-amber/10 text-status-amber text-[10px] uppercase">
            new
          </span>
        )}
      </td>
      <td className="py-2.5 px-3 text-sm text-gray-300" title={property.address || ""}>
        <span className="capitalize">{(quote.transaction_type || "").replace(/_/g, " ")}</span>
        {property.address && (
          <span className="text-gray-500"> — {truncateAddress(property.address)}</span>
        )}
      </td>
      <td className="py-2.5 px-3"><FeChip initials={feeEarner.initials} /></td>
      <td className="py-2.5 px-3 text-sm text-white">
        {fmtGbp(totalEx)}<span className="text-gray-500 text-xs"> +VAT</span>
      </td>
      <td className="py-2.5 px-3 text-xs">{fmtTimeOrDate(quote.sent?.sent_at)}</td>
      <td className="py-2.5 px-3"><ReceivedCell receipt={receipt} /></td>
      <td className="py-2.5 px-3" onClick={(e) => e.stopPropagation()}>
        <AcceptedCell quote={quote} onMarkAccepted={onMarkAccepted} busy={busy} />
      </td>
      <td className="py-2.5 px-3" onClick={(e) => e.stopPropagation()}>
        <NextStepCell quote={quote} navigate={navigate} />
      </td>
      <td className="py-2.5 px-3 text-right"><ChevronRight className="w-4 h-4 text-gray-500 inline" /></td>
    </tr>
  );
}

export default function Quotes() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [receipts, setReceipts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [busyRef, setBusyRef] = useState(null);
  const [search, setSearch] = useState("");
  const [feeEarnerFilter, setFeeEarnerFilter] = useState("");
  const [showTest, setShowTest] = useState(false);   // BM-H85
  const [chipState, setChipState] = useState(() =>
    FILTER_CHIPS.reduce((acc, c) => { acc[c.key] = c.defaultOn; return acc; }, {}));
  const pollRef = useRef(null);

  const activeFEs = useMemo(
    () => STAFF.filter(s => s.is_active && (s.is_fee_earner || s.is_fe_dropdown))
              .sort((a, b) => a.full_name.localeCompare(b.full_name)),
    [],
  );

  const fetchQuotes = useCallback(async (isPoll = false) => {
    if (isPoll) setRefreshing(true); else setLoading(true);
    setError("");
    try {
      const params = { limit: 200 };
      if (search.trim()) params.q = search.trim();
      if (feeEarnerFilter) params.fee_earner = feeEarnerFilter;
      if (showTest) params.include_test = true;   // BM-H85
      const res = await axios.get(`${API}/hub/quotes`, {
        headers: authHeaders(), params,
      });
      const items = res.data?.items || [];
      setRows(items);
      // Batch-fetch receipts for any row that has been sent.
      const tokens = items
        .map(q => q.sent?.tracking_token).filter(Boolean);
      if (tokens.length) {
        try {
          const r = await axios.post(`${API}/hub/quotes/receipts/batch`,
            { tokens }, { headers: authHeaders() });
          setReceipts(r.data?.receipts || {});
        } catch (recErr) {
          // A missing receipts endpoint / collection should never break
          // the tracker — the Received column just stays blank, which
          // matches the verbatim tooltip's own guidance.
          setReceipts({});
        }
      } else {
        setReceipts({});
      }
    } catch (err) {
      setError(errorMessage(err, "Failed to load quotes."));
      if (!isPoll) toast.error("Failed to load quotes.");
    } finally {
      if (isPoll) setRefreshing(false); else setLoading(false);
    }
  }, [search, feeEarnerFilter, showTest]);

  useEffect(() => { fetchQuotes(false); }, [fetchQuotes]);

  // 60s poll while mounted — mirror PaymentTracker's cadence so a fee
  // earner watching for a client reply doesn't have to hit refresh.
  useEffect(() => {
    pollRef.current = setInterval(() => { fetchQuotes(true); }, REFRESH_INTERVAL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchQuotes]);

  const visibleRows = useMemo(() => {
    // If every chip is off, treat that as "show everything" (V's rule).
    const anyOn = Object.values(chipState).some(Boolean);
    const enrichedRows = rows.map(q => ({
      ...q,
      _state: deriveRowState(q),
    }));
    const filtered = enrichedRows.filter(q => {
      if (!anyOn) return true;
      const chips = rowStateChips(q._state);
      return chips.some(c => chipState[c]);
    });
    filtered.sort((a, b) => {
      const rA = priorityRank(a._state);
      const rB = priorityRank(b._state);
      if (rA !== rB) return rA - rB;
      // Within a bucket, most-recently-created first.
      const tA = new Date(a.created_at || 0).valueOf();
      const tB = new Date(b.created_at || 0).valueOf();
      return tB - tA;
    });
    return filtered;
  }, [rows, chipState]);

  async function onMarkAccepted(quote) {
    if (busyRef) return;
    setBusyRef(quote.quote_ref);
    try {
      await axios.post(`${API}/hub/quotes/${quote.quote_ref}/mark-accepted`,
        { accepted_via: "manual" }, { headers: authHeaders() });
      toast.success("Quote marked accepted. Next step: run conflict check.");
      await fetchQuotes(true);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to mark accepted."));
    } finally {
      setBusyRef(null);
    }
  }

  return (
    <div className="min-h-screen bg-navy p-6 text-white" data-testid="quotes-tracker-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/")}
              data-testid="quotes-tracker-back"
              className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal" />
              <h1 className="text-xl font-bold">Quotes</h1>
              {refreshing && <Loader2 className="w-3 h-3 animate-spin text-teal ml-2" />}
            </div>
          </div>
          <Button onClick={() => navigate("/quotes/new")}
            data-testid="quotes-tracker-new"
            className="bg-teal text-black hover:bg-teal-hover">
            <Plus className="w-4 h-4 mr-1" /> New quote
          </Button>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="quotes-tracker-chips">
          {FILTER_CHIPS.map(chip => (
            <button key={chip.key} onClick={() =>
              setChipState(s => ({ ...s, [chip.key]: !s[chip.key] }))}
              data-testid={`quotes-tracker-chip-${chip.key}`}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                chipState[chip.key]
                  ? "bg-teal/15 text-teal border-teal/40"
                  : "bg-navy-card text-gray-400 border-white/10 hover:border-white/20"
              }`}>
              {chip.label}
            </button>
          ))}
        </div>

        {/* Search + FE filter + refresh */}
        <div className="bg-navy-card border border-white/10 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search ref, client, address..."
                data-testid="quotes-tracker-search"
                className="w-full bg-navy border border-white/10 rounded pl-9 pr-3 py-2 text-sm text-white" />
            </div>
            <select value={feeEarnerFilter} onChange={(e) => setFeeEarnerFilter(e.target.value)}
              data-testid="quotes-tracker-fe-filter"
              className="bg-navy border border-white/10 rounded px-3 py-2 text-sm text-white">
              <option value="">All fee earners</option>
              {activeFEs.map(fe => (
                <option key={fe.email} value={fe.email}>{fe.full_name}</option>
              ))}
            </select>
            {/* BM-H85 — test quotes are hidden by default. */}
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input type="checkbox" checked={showTest}
                onChange={(e) => setShowTest(e.target.checked)}
                data-testid="quotes-tracker-show-test"
                className="accent-amber-500" />
              Show test quotes
            </label>
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => fetchQuotes(false)} disabled={loading}
                data-testid="quotes-tracker-refresh"
                className="text-xs text-gray-400 hover:text-white">
                {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                         : <RefreshCw className="w-3 h-3 mr-1" />}
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="quotes-tracker-loading">
            <Loader2 className="w-6 h-6 animate-spin text-teal" />
          </div>
        ) : error ? (
          <div className="bg-status-red-dim/20 border border-status-red/40 rounded-md p-4 text-sm text-status-red"
            data-testid="quotes-tracker-error">
            {error}
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="bg-navy-card border border-white/10 rounded-lg p-12 text-center"
            data-testid="quotes-tracker-empty">
            <Clock className="w-8 h-8 mx-auto text-gray-500 mb-2" />
            <p className="text-sm text-gray-400">No quotes match the current filters.</p>
          </div>
        ) : (
          <div className="bg-navy-card border border-white/10 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="quotes-tracker-table">
                <thead>
                  <tr className="text-left text-xs text-gray-400 uppercase tracking-wider border-b border-white/10">
                    <th className="py-2 px-3">Ref</th>
                    <th className="py-2 px-3">Client</th>
                    <th className="py-2 px-3">Matter</th>
                    <th className="py-2 px-3">FE</th>
                    <th className="py-2 px-3">Value</th>
                    <th className="py-2 px-3">Sent</th>
                    <th className="py-2 px-3"><ReceivedHeader /></th>
                    <th className="py-2 px-3">Accepted</th>
                    <th className="py-2 px-3">Next step</th>
                    <th className="py-2 px-3" />
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(q => (
                    <TrackerRow
                      key={q.quote_ref}
                      quote={q}
                      receipt={receipts[q.sent?.tracking_token]}
                      navigate={navigate}
                      onMarkAccepted={onMarkAccepted}
                      busyRef={busyRef}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
